import {
  OrderByInput,
  PermissionValues,
  SUBSCRIPTION_TIERS,
  WorkspaceFilterModel,
  cacheKeys,
  useCurrentAccount,
  useCustomMutation,
  useFindAssetIdForWorkspace,
  useImpersonateWorkspace,
  useSetWorkspaceListOrder,
  useWorkspaceFilter,
  useWorkspaceListOrder,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  Colors,
  ColumnData,
  InfiniteDataGrid,
  ListCountProvider,
  LoadingIndicator,
  SelectionStatus,
  SlideAction,
  SlideLayout,
  ViewMode,
  getSubscriptionTierColumn,
  type SlideLayoutLocationState,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { AssetCard } from '../../assets/components/assetCard';
import { AssetFilters } from '../../assets/components/assetFilters';
import { WorkspacesFilters } from './workspacesFilters';
import { WorkspacesSelectionPanelMain } from './workspacesSelectionPanel';

type WorkspacesProps = {
  /**
   * Optional right-column override used by sibling routes (e.g. the
   * filters page) to inject their own panel while reusing the workspaces
   * list on the left. When omitted, the right column is driven by the
   * `workspaces/:id` wildcard param.
   */
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
};

export const Workspaces = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: WorkspacesProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { '*': workspaceId } = useParams();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const { assetId: workspaceAssetId, isLoading: isWorkspaceAssetLoading } =
    useFindAssetIdForWorkspace(workspaceId ?? '');
  const { t, i18n } = useTranslation();
  const { account } = useCurrentAccount();
  const { impersonateWorkspace } = useImpersonateWorkspace();
  const queryClient = useQueryClient();
  const [workspacesSelected, setWorkspacesSelected] = useState<string[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');
  const hasSelection = workspacesSelected.length > 0;
  const updateWorkspaceSelection = (rows: any) => {
    setWorkspacesSelected(rows.map((row: any) => row.id));
  };
  const clearSelection = useCallback(() => {
    setWorkspacesSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);
  const selectedIdsSet = useMemo(
    () => new Set(workspacesSelected),
    [workspacesSelected]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const { mutateAsync: updateSubscriptionTier } = useCustomMutation({
    query: `
      mutation ChangeSubscriptionTier($input: ChangeSubscriptionTierInput!) {
        workspace {
          changeSubscriptionTier(input: $input) {
            workspaceId
          }
        }
      }`,
  });

  const { data: workspaceOrder, isLoading: orderLoading } =
    useWorkspaceListOrder();
  const { mutateAsync: setWorkspaceOrder } = useSetWorkspaceListOrder();
  const columnHelper = createColumnHelper<any>();
  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setWorkspaceOrder(orderBy);
  };
  const { data: workspaceFilter } = useWorkspaceFilter();
  const filter = useMemo(() => {
    const filter: WorkspaceFilterModel =
      workspaceFilter ?? new WorkspaceFilterModel();
    return filter.getGraphQLFilter();
  }, [workspaceFilter]);

  const checkboxColumn = columnHelper.accessor('selected', {
    cell: BaseColumnCheckbox.checkbox,
    header: BaseColumnCheckbox.checkboxHeader,
    meta: { style: styles.checkboxCell },
  });

  const allColumns = useMemo(
    () => ({
      name: columnHelper.accessor('name', {
        cell: ({ row }) => {
          const original = row.original;
          const onPress = async () => {
            await impersonateWorkspace({
              id: original.id,
              name: original.name,
              type: original.type,
            });
            window.location.href = '/activities';
          };
          return (
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={styles.nameCell}
            >
              <Button
                mode="text"
                onPress={onPress}
                style={styles.nameButton}
                labelStyle={styles.nameLabel}
              >
                {original.name}
              </Button>
            </Text>
          );
        },
        header: () => t('workspace.list.name'),
        meta: { sortable: true },
      }),
      owner: columnHelper.accessor('owner', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.owner'),
        meta: { sortable: true },
      }),
      type: columnHelper.accessor('type', {
        cell: ({ row }) => {
          const original = row.original;
          return (
            <Text>
              {original.type ? t(`workspace.types.${original.type}`) : ''}
            </Text>
          );
        },
        header: () => t('workspace.list.type'),
        meta: { sortable: true },
      }),
      subscriptionTierId: columnHelper.accessor('subscriptionTierId', {
        cell: (info) =>
          getSubscriptionTierColumn(
            info,
            SUBSCRIPTION_TIERS,
            async ({ workspaceId, tierId }) => {
              await updateSubscriptionTier({
                input: {
                  workspaceId,
                  tierId,
                },
              });
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('workspace').all,
              });
            }
          ),
        header: () => t('workspace.filter.columns.subscriptionTierId'),
        meta: { sortable: true },
      }),
      assetsCount: columnHelper.accessor('assetsCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.assetsCount'),
        meta: { sortable: true },
      }),
      routinesCount: columnHelper.accessor('routinesCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.routinesCount'),
        meta: { sortable: true },
      }),
      alerts: columnHelper.accessor('alerts', {
        cell: (info) => <Text>{info.getValue() || 0}</Text>,
        header: () => t('workspace.list.alerts'),
        meta: { sortable: true },
      }),
      peopleCount: columnHelper.accessor('peopleCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.peopleCount'),
        meta: { sortable: true },
      }),
      activitiesCount: columnHelper.accessor('activitiesCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.activitiesCount'),
        meta: { sortable: true },
      }),
      groupsCount: columnHelper.accessor('groupsCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.groupsCount'),
        meta: { sortable: true },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.createdAt'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      createdDate: columnHelper.accessor('createdDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.createdDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.createdDate'),
          exportCell: (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      createdTime: columnHelper.accessor('createdTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.createdTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.createdTime'),
          exportCell: (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      createdBy: columnHelper.accessor('createdBy', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.createdBy'),
        meta: { sortable: true },
      }),
      lastScanned: columnHelper.accessor('lastScanned', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.lastScanned'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      lastScannedDate: columnHelper.accessor('lastScannedDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastScannedDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastScannedDate'),
          exportCell: (data: any) => {
            if (!data.lastScanned) return '';
            return new Date(data.lastScanned).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      lastScannedTime: columnHelper.accessor('lastScannedTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastScannedTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastScannedTime'),
          exportCell: (data: any) => {
            if (!data.lastScanned) return '';
            return new Date(data.lastScanned)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      updatedAt: columnHelper.accessor('updatedAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.updatedAt'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      updatedDate: columnHelper.accessor('updatedDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.updatedDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.updatedDate'),
          exportCell: (data: any) => {
            if (!data.updatedAt) return '';
            return new Date(data.updatedAt).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      updatedTime: columnHelper.accessor('updatedTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.updatedTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.updatedTime'),
          exportCell: (data: any) => {
            if (!data.updatedAt) return '';
            return new Date(data.updatedAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      lastActivity: columnHelper.accessor('lastActivity', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.lastActivity'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      lastActivityDate: columnHelper.accessor('lastActivityDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastActivityDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastActivityDate'),
          exportCell: (data: any) => {
            if (!data.lastActivity) return '';
            return new Date(data.lastActivity).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      lastActivityTime: columnHelper.accessor('lastActivityTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastActivityTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastActivityTime'),
          exportCell: (data: any) => {
            if (!data.lastActivity) return '';
            return new Date(data.lastActivity)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
    }),
    [t, navigate, impersonateWorkspace, columnHelper]
  );

  const { columns, exportColumns } = useMemo(() => {
    // Define the correct column order
    const columnOrder: (keyof typeof allColumns)[] = [
      'owner',
      'type',
      'subscriptionTierId',
      'assetsCount',
      'routinesCount',
      'alerts',
      'peopleCount',
      'activitiesCount',
      'groupsCount',
      'createdAt',
      'lastScanned',
      'updatedAt',
      'lastActivity',
    ];

    // Build columns in the correct order
    const columnList: (typeof allColumns)[keyof typeof allColumns][] = [];
    if (workspaceFilter?.columns) {
      // Add columns in the defined order, only if they're in the filter
      columnOrder.forEach((key) => {
        if (workspaceFilter.columns.includes(key) && allColumns[key]) {
          columnList.push(allColumns[key]);
        }
      });
    } else {
      // If no filter, use default order
      columnOrder.forEach((key) => {
        if (allColumns[key]) {
          columnList.push(allColumns[key]);
        }
      });
    }

    // Always include export-only columns for data export
    const exportOnlyColumns = [
      allColumns.createdDate,
      allColumns.createdTime,
      allColumns.lastScannedDate,
      allColumns.lastScannedTime,
      allColumns.updatedDate,
      allColumns.updatedTime,
      allColumns.lastActivityDate,
      allColumns.lastActivityTime,
    ];

    // Full set for export (includes hidden/export-only)
    const allColumnsForExport = [
      checkboxColumn,
      allColumns.name,
      ...columnList,
      ...exportOnlyColumns,
    ];

    // Filter out hidden columns from grid display but keep them for export
    const gridColumns = allColumnsForExport.filter(
      (column) => !(column.meta as any)?.hidden
    );

    return {
      columns: gridColumns,
      exportColumns: allColumnsForExport,
    };
  }, [workspaceFilter?.columns, allColumns, checkboxColumn]);

  useEffect(() => {
    if (!account) return;

    if (!account.hasPermission(PermissionValues.globalAdmin)) {
      navigate(`/accounts/${account.workspace?.id}`);
    }
  }, [account, navigate]);

  const headerComponent = <WorkspacesFilters />;
  const [count, setCount] = useState<number>();

  const columnComponent = (
    <WorkspacesGrid
      columns={columns}
      exportColumns={exportColumns}
      filter={filter}
      workspaceOrder={workspaceOrder}
      onOrderByChange={onOrderByChange}
      updateWorkspaceSelection={updateWorkspaceSelection}
      isRowSelected={isRowSelected}
      selectionStatus={selectionStatus}
      onSelectionStatusChange={setSelectionStatus}
      onItemsCountChange={setCount}
    />
  );

  const routeContext =
    workspaceId && workspaceAssetId
      ? {
          basePath: `/workspaces/${workspaceId}/asset/${workspaceAssetId}`,
          listPath: '/workspaces',
          isWorkspaceMode: true,
        }
      : undefined;

  const detailRightColumn = {
    title: 'admin.menu.workspaces',
    mainComponent: isWorkspaceAssetLoading ? (
      <LoadingIndicator />
    ) : workspaceAssetId && routeContext ? (
      <AssetRouteProvider value={routeContext}>
        <AssetCard assetId={workspaceAssetId} />
      </AssetRouteProvider>
    ) : null,
    headerComponent:
      !isWorkspaceAssetLoading && workspaceAssetId && routeContext ? (
        <AssetRouteProvider value={routeContext}>
          <AssetFilters assetId={workspaceAssetId} />
        </AssetRouteProvider>
      ) : null,
    showBackButton: true,
    backButtonTransition: SlideAction.SINGLE,
    onBackButtonPress: () => {
      navigate('/workspaces');
    },
  };

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = workspacesSelected.length;
    const title =
      count === 1
        ? t('workspace.topBar.workspaceSelected', {
            defaultValue: '1 workspace selected',
          })
        : t('workspace.topBar.workspacesSelected', {
            count,
            defaultValue: `${count} workspaces selected`,
          });

    return {
      title,
      mainComponent: (
        <WorkspacesSelectionPanelMain workspaceIds={workspacesSelected} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [hasSelection, workspacesSelected, t, clearSelection]);

  // Register the computed title as its own translation key so downstream
  // consumers that translate the SlideLayout title can resolve it. We do
  // this in an effect (rather than during render) to avoid mutating the
  // i18n resource store on every render.
  useEffect(() => {
    const title = selectionRightColumn?.title;
    if (!title) return;
    i18n.addResource(i18n.language, 'translation', title, title);
  }, [selectionRightColumn, i18n]);

  // Selection panel takes priority over detail/override panels.
  const resolvedRightColumn =
    selectionRightColumn ??
    (workspaceId ? detailRightColumn : rightColumnOverride);
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || workspaceId || rightColumnOverride
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  // Wait for orderBy to load from storage before rendering the grid to prevent
  // a double query — the grid fires immediately on mount, so if orderBy hasn't
  // been restored yet it would issue one request with the default order and
  // then another once the persisted value is available.
  if (orderLoading) {
    return <LoadingIndicator />;
  }

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={{
          title: 'admin.menu.workspaces',
          count,
          mainComponent: columnComponent,
          headerComponent: headerComponent,
          disableScroll: true,
        }}
        rightColumn={resolvedRightColumn}
        viewMode={resolvedViewMode}
        initialTransition={initialTransition}
      />
    </ListCountProvider>
  );
};

const GET_WORKSPACES = `
  query GetWorkspaces($filter: ListWorkspacesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    workspace {
      listWorkspaces(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        offset
        limit
        items {
          id
          name
          type
          owner
          subscriptionTierId
          assetsCount
          routinesCount
          alerts
          peopleCount
          activitiesCount
          groupsCount
          createdAt
          createdBy
          lastScanned
          updatedAt
          lastActivity
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  workspacesContainer: {
    flex: 1,
    borderRadius: 4,
    overflow: 'hidden',
  },
  export: {
    flexDirection: 'row',
    margin: 5,
  },
  exportAllAccountsContainer: {
    flexDirection: 'column',
    alignSelf: 'flex-end',
  },
  editColumn: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    width: 30,
    maxWidth: 30,
    marginLeft: 50,
  },
  editModal: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 4,
    backgroundColor: Colors.blackInactive,
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameCell: {
    overflow: 'hidden',
    maxWidth: 200,
  },
  nameButton: {
    maxWidth: '100%',
  },
  nameLabel: {
    overflow: 'hidden',
  },
});

type WorkspacesGridProps = {
  columns: any[];
  exportColumns: any[];
  filter: any;
  workspaceOrder: OrderByInput | undefined;
  onOrderByChange: (orderBy: OrderByInput) => Promise<void>;
  updateWorkspaceSelection: (rows: any[]) => void;
  isRowSelected: (row: any) => boolean;
  selectionStatus: SelectionStatus;
  onSelectionStatusChange: (status: SelectionStatus) => void;
  onItemsCountChange?: (count: number) => void;
};

const WorkspacesGrid = ({
  columns,
  exportColumns,
  filter,
  workspaceOrder,
  onOrderByChange,
  updateWorkspaceSelection,
  isRowSelected,
  selectionStatus,
  onSelectionStatusChange,
  onItemsCountChange,
}: WorkspacesGridProps) => {
  const { currentViewMode, onSlideChange } = useSlideLayout();
  const navigate = useNavigate();

  const handleRowClick = async (row: any) => {
    if (currentViewMode === ViewMode.SINGLE) {
      await onSlideChange(SlideAction.DUAL);
    }
    navigate(`/workspaces/${row.id}`);
  };

  return (
    <View style={styles.workspacesContainer}>
      <InfiniteDataGrid
        columns={columns}
        exportColumns={exportColumns}
        query={GET_WORKSPACES}
        dataPrefix="workspace.listWorkspaces"
        modelName="workspace"
        filter={filter}
        orderBy={workspaceOrder}
        pageSize={50}
        hideHeader={false}
        showScrollIndicator={true}
        rowTestIdPrefix="workspace-row"
        onOrderByChange={onOrderByChange}
        updateRowsSelected={(rows) => updateWorkspaceSelection(rows)}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        exportFileName="workspaces"
        onRowClick={handleRowClick}
        onItemsCountChange={onItemsCountChange}
      />
    </View>
  );
};
